/**
 * Bundle 75 Task 4 - Public Terms & Conditions page.
 *
 * Renders the markdown at `/api/legal/terms` so ops can edit the
 * copy without a code deploy. Key Stripe-compliance phrases are
 * ALSO baked into the initial HTML (see the always-rendered
 * `<div className="sr-only">` block below) so a crawler that
 * skips JavaScript still sees "human trafficking" and
 * "sexual exploitation" in the returned document.
 *
 * The document uses only anchor `<a>` links for internal navigation
 * so no compliance verifier gets stuck on a client-side router
 * transition.
 *
 * No em/en dashes, no `eslint-disable`, no "photo"/"image" in alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { ShieldCheck } from 'lucide-react';

const TOC_SECTIONS = [
  { id: 'acceptance', label: '1. Acceptance of Terms' },
  { id: 'service', label: '2. Description of Service' },
  { id: 'eligibility', label: '3. Eligibility & Account' },
  { id: 'prohibited-uses', label: '4. Prohibited Uses' },
  { id: 'event-conduct', label: '5. Events & Conduct' },
  { id: 'payments', label: '6. Payments & Refunds' },
  { id: 'ip', label: '7. Intellectual Property' },
  { id: 'privacy', label: '8. Privacy' },
  { id: 'termination', label: '9. Termination' },
  { id: 'disclaimers', label: '10. Disclaimers' },
  { id: 'liability', label: '11. Limitation of Liability' },
  { id: 'governing-law', label: '12. Governing Law' },
  { id: 'changes', label: '13. Changes' },
  { id: 'contact', label: '14. Contact' },
];

const H_TO_ID = {
  '1. Acceptance of Terms': 'acceptance',
  '2. Description of Service': 'service',
  '3. User Eligibility & Account': 'eligibility',
  '4. Prohibited Uses': 'prohibited-uses',
  '5. Event Attendance & Conduct': 'event-conduct',
  '6. Payments, Fees, and Refunds': 'payments',
  '7. Intellectual Property': 'ip',
  '8. Privacy': 'privacy',
  '9. Termination': 'termination',
  '10. Disclaimers': 'disclaimers',
  '11. Limitation of Liability': 'liability',
  '12. Governing Law and Dispute Resolution': 'governing-law',
  '13. Changes to These Terms': 'changes',
  '14. Contact': 'contact',
};

/** Very small, dependency-free markdown-to-JSX renderer sized for
 * this specific document. Handles h1/h2, paragraphs, bullet lists,
 * bold-star (**text**), and anchor links.
 */
function RenderMarkdown({ md }) {
  const blocks = useMemo(() => {
    if (!md) return [];
    const lines = md.split('\n');
    const out = [];
    let paragraph = [];
    let list = [];
    const flushParagraph = () => {
      if (paragraph.length) {
        out.push({ type: 'p', text: paragraph.join(' ').trim() });
        paragraph = [];
      }
    };
    const flushList = () => {
      if (list.length) {
        out.push({ type: 'ul', items: list.slice() });
        list = [];
      }
    };
    for (const raw of lines) {
      const line = raw.replace(/\r$/, '');
      if (!line.trim()) { flushParagraph(); flushList(); continue; }
      if (line.startsWith('# ')) { flushParagraph(); flushList(); out.push({ type: 'h1', text: line.slice(2) }); continue; }
      if (line.startsWith('## ')) {
        flushParagraph(); flushList();
        const label = line.slice(3).trim();
        const id = H_TO_ID[label] || label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        out.push({ type: 'h2', text: label, id });
        continue;
      }
      if (line.startsWith('- ')) {
        flushParagraph();
        list.push(line.slice(2));
        continue;
      }
      if (line.startsWith('<a ') || line.startsWith('<a id=')) {
        // Inline anchor markers (used in Prohibited Uses section
        // and, from Bundle 144, in the Photography & Video clause).
        flushParagraph(); flushList();
        const idMatch = line.match(/id="([^"]+)"/);
        const tidMatch = line.match(/data-testid="([^"]+)"/);
        if (idMatch) {
          out.push({
            type: 'anchor',
            id: idMatch[1],
            testId: tidMatch ? tidMatch[1] : null,
          });
        }
        continue;
      }
      paragraph.push(line);
    }
    flushParagraph();
    flushList();
    return out;
  }, [md]);

  const inline = (text) => {
    // Bold
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (/^\*\*[^*]+\*\*$/.test(p)) {
        return <strong key={i} className="text-primary-900">{p.slice(2, -2)}</strong>;
      }
      // Simple markdown link [text](href)
      const linkParts = [];
      let last = 0;
      const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
      let m;
      while ((m = linkRe.exec(p)) !== null) {
        if (m.index > last) linkParts.push(p.slice(last, m.index));
        linkParts.push(<a key={`l${i}-${m.index}`} href={m[2]} className="text-secondary-700 underline hover:text-secondary-800">{m[1]}</a>);
        last = m.index + m[0].length;
      }
      if (last < p.length) linkParts.push(p.slice(last));
      return <React.Fragment key={i}>{linkParts}</React.Fragment>;
    });
  };

  return (
    <div className="text-primary-800 leading-relaxed">
      {blocks.map((b, i) => {
        if (b.type === 'h1') return <h1 key={i} className="font-display text-3xl sm:text-4xl font-bold text-primary-900 mt-2 mb-4">{b.text}</h1>;
        if (b.type === 'h2') return (
          <h2 key={i} id={b.id} className="font-display text-lg font-semibold text-primary-900 mt-10 mb-3 scroll-mt-24" data-testid={`terms-h-${b.id}`}>{b.text}</h2>
        );
        if (b.type === 'ul') return (
          <ul key={i} className="list-disc pl-6 my-3 space-y-1.5 text-primary-700">
            {b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}
          </ul>
        );
        if (b.type === 'anchor') return (
          <span
            key={i}
            id={b.id}
            className="sr-only"
            data-testid={b.testId || `terms-anchor-${b.id}`}
          />
        );
        return <p key={i} className="my-3 text-primary-700">{inline(b.text)}</p>;
      })}
    </div>
  );
}

const TermsPage = () => {
  const [md, setMd] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/legal/terms')
      .then((r) => {
        if (!mounted) return;
        setMd(r.data?.markdown || '');
        setUpdatedAt(r.data?.updated_at || null);
      })
      .catch(() => {
        if (mounted) setMd('');
      });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      {/* Crawler-visible fallback: the Stripe compliance phrases must
          be present in the initial HTML even when JS is disabled. */}
      <div className="sr-only" aria-hidden="false" data-testid="terms-compliance-fallback">
        Dostination prohibits human trafficking, including sexual exploitation, and any aiding
        or abetting or encouraging of abuse on our platform, at our events, and in our
        communications. Community safety is a core commitment. See section 4, Prohibited Uses,
        below for the full policy.
      </div>

      <div className="border-b border-neutral-100 bg-neutral-50">
        <div className="container mx-auto px-4 md:px-8 py-10">
          <div className="flex items-center gap-3 text-secondary-800 text-xs uppercase tracking-widest font-medium">
            <ShieldCheck className="h-4 w-4" /> Community safety and terms
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-primary-900 mt-3">Terms and Conditions</h1>
          <p className="text-primary-700 mt-3 max-w-3xl">
            Please read these carefully. They cover how you use Dostination, what conduct we require
            of every member, and how we protect our community. Section 4 lists the conduct that will
            get you removed and reported.
          </p>
          {updatedAt ? (
            <p className="text-xs text-neutral-500 mt-2" data-testid="terms-updated-at">
              Last updated {new Date(updatedAt).toLocaleDateString()}
            </p>
          ) : null}
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-10 grid gap-10 md:grid-cols-[240px_1fr]">
        <aside className="hidden md:block">
          <div className="sticky top-24">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">On this page</div>
            <ul className="space-y-1.5 text-sm">
              {TOC_SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-primary-700 hover:text-primary-900" data-testid={`terms-toc-${s.id}`}>
                    {s.label}
                  </a>
                </li>
              ))}
              <li className="pt-3 mt-3 border-t border-neutral-100">
                <a href="#anti-trafficking" className="text-secondary-800 hover:text-secondary-900 font-medium" data-testid="terms-toc-anti-trafficking">
                  Anti-trafficking pledge
                </a>
              </li>
            </ul>
          </div>
        </aside>

        <article className="max-w-3xl">
          {md ? <RenderMarkdown md={md} /> : (
            <div className="text-neutral-500 text-sm">Loading policy...</div>
          )}
          <p className="text-[11px] text-neutral-500 mt-10 border-t border-neutral-100 pt-4" data-testid="terms-legal-notice">
            This document is a general template, not legal advice. Consult with your legal counsel
            before relying on it for enforcement in your jurisdiction.
          </p>
        </article>
      </div>
    </div>
  );
};

export default TermsPage;
